import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import PersonalInfo from './formSteps/PersonalInfo';
import Education from './formSteps/Education';
import Experience from './formSteps/Experience';
import Competences from './formSteps/Competences';
import Documents from './formSteps/Documents';
import SiteFooter, { TrustCard } from './SiteFooter';
import { validateAll, errorsByStep, FIELD_STEP, FIELD_LABELS } from './formValidation';
import { submitCandidature } from '../services/api';
import useCampagne, { statutCampagne } from '../hooks/useCampagne';

const STEPS = [
  { id: 1, label: 'Identité', component: PersonalInfo },
  { id: 2, label: 'Formation', component: Education },
  { id: 3, label: 'Expérience', component: Experience },
  { id: 4, label: 'Compétences', component: Competences },
  { id: 5, label: 'Documents', component: Documents },
];
const RECAP_STEP = STEPS.length + 1;

const MISSIONS = [
  'Contribuer à la formation des agents de collecte',
  'Coordonner, suivre et superviser les opérations de terrain de la province',
  "Veiller au respect de la méthodologie et à la qualité des données collectées",
  "Identifier les difficultés, prendre les mesures correctives et en référer à l'équipe centrale",
];

const CRITERES = [
  { text: "Diplôme de statisticien ou de démographe, niveau licence, master ou équivalent", obligatoire: true },
  { text: "Expérience dans la collecte, la supervision ou la coordination d'enquêtes statistiques auprès des ménages", obligatoire: true },
  { text: "Expérience avérée dans la gestion et la supervision d'équipes de terrain", obligatoire: true },
  { text: "Maîtrise d'un outil de collecte électronique sur tablette ou smartphone (Survey Solutions, CSPro, KoboCollect, ODK…)", obligatoire: true },
  { text: "Bonne connaissance des méthodes d'échantillonnage, de collecte et de contrôle de qualité des données" },
  { text: "Capacité à organiser le déploiement des équipes, suivre leur progression et résoudre les difficultés opérationnelles" },
  { text: "Capacité à vérifier la cohérence, la complétude et la qualité des données avant transmission" },
  { text: "Disponibilité pendant toute la période de formation, de collecte et de clôture de l'enquête" },
  { text: "Aptitude aux déplacements fréquents, y compris en zones rurales ou difficiles d'accès" },
  { text: "Rigueur, sens de l'organisation, intégrité, confidentialité et capacité à travailler sous pression" },
  { text: "Bonnes capacités de communication et de coordination avec les autorités administratives et sanitaires et les équipes de terrain" },
  { text: "Une expérience des enquêtes de santé, de vaccination, EDS, MICS ou similaires", atout: true },
  { text: "Les candidatures féminines sont fortement encouragées" },
];

const initialData = {
  nom: '', prenoms: '', sexe: '', date_naissance: '', telephone: '', telephone_2: '', email: '',
  niveau_etude: '', domaine_formation: '', diplome_obtenu: '',
  experience_enquetes_menages: null, annees_experience: 0, roles_enquetes: [], operations: [],
  description_experience: '',
  experience_gestion_equipes: null, nombre_agents_encadres: 0,
  outils_collecte: [], nombre_operations_tablette: 0,
  connaissance_echantillonnage: false, connaissance_controle_qualite: false,
  capacite_deploiement_equipes: false, capacite_verification_donnees: false,
  disponibilite_totale: false, apte_deplacements: false,
  experience_relations_autorites: false, engagement_ethique: false,
  cv: null, diplome_fichier: null, lettre_motivation: null, piece_identite: null,
};

const FILE_FIELDS = ['cv', 'diplome_fichier', 'lettre_motivation', 'piece_identite'];
const LIST_FIELDS = ['roles_enquetes', 'operations', 'outils_collecte'];
const DRAFT_KEY = 'ecv2026_brouillon';

// Brouillon local (sans les fichiers, que le navigateur ne peut pas conserver)
const loadDraft = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY));
    if (!saved) return null;
    // Seuls les champs actuels sont repris (un ancien brouillon peut contenir des champs supprimés)
    const champs = Object.fromEntries(Object.entries(saved).filter(([k]) => k in initialData));
    return { ...initialData, ...champs };
  } catch {
    return null;
  }
};

const saveDraft = (data) => {
  try {
    const draft = Object.fromEntries(Object.entries(data).filter(([k]) => !FILE_FIELDS.includes(k)));
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch { /* stockage indisponible : on continue sans brouillon */ }
};

const clearDraft = () => {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
};

const RecruitmentForm = () => {
  const { campagne, loading: campagneLoading } = useCampagne();
  const [draft, setDraft] = useState(loadDraft);
  const [step, setStep] = useState(0); // 0 = avis
  const [formData, setFormData] = useState(draft || initialData);
  const [visited, setVisited] = useState(() => new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [numeroDossier, setNumeroDossier] = useState('');

  const validationErrors = useMemo(() => validateAll(formData), [formData]);
  const missingByStep = useMemo(() => errorsByStep(validationErrors), [validationErrors]);
  const completedSteps = STEPS.filter(s => !missingByStep[s.id]).length;
  const isComplete = completedSteps === STEPS.length;

  useEffect(() => {
    if (step > 0 && !numeroDossier) saveDraft(formData);
  }, [formData, step, numeroDossier]);

  // Les erreurs d'une étape ne s'affichent qu'une fois l'étape quittée (ou à la soumission)
  const showErrorsFor = (s) => visited.has(s) || submitAttempted;
  const stepErrors = {};
  Object.entries(validationErrors).forEach(([field, msg]) => {
    if (showErrorsFor(FIELD_STEP[field] || 1)) stepErrors[field] = msg;
  });
  const errors = { ...stepErrors, ...serverErrors };

  const handleChange = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    if (Object.keys(serverErrors).length) {
      setServerErrors(prev => {
        const next = { ...prev };
        Object.keys(updates).forEach(k => delete next[k]);
        return next;
      });
    }
  };

  const goTo = (target) => {
    if (step >= 1 && step <= STEPS.length) {
      setVisited(prev => new Set(prev).add(step));
    }
    setStep(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!isComplete) {
      goTo(Number(Object.keys(missingByStep)[0]));
      return;
    }
    const statut = statutCampagne(campagne);
    if (statut !== 'ouverte') {
      toast.error(statut === 'a_venir'
        ? "Le dépôt des candidatures n'est pas encore ouvert."
        : 'La date limite de dépôt est dépassée.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (FILE_FIELDS.includes(key)) fd.append(key, value);
        else if (LIST_FIELDS.includes(key)) fd.append(key, JSON.stringify(value));
        else if (typeof value === 'string') fd.append(key, value.trim());
        else fd.append(key, value);
      });
      const result = await submitCandidature(fd);
      clearDraft();
      setNumeroDossier(result.numero_dossier);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        const errs = {};
        Object.entries(data).forEach(([key, value]) => {
          errs[key] = Array.isArray(value) ? value.join(' ') : String(value);
        });
        setServerErrors(errs);
        const steps = Object.keys(errs).filter(k => FIELD_STEP[k]).map(k => FIELD_STEP[k]);
        if (steps.length) goTo(Math.min(...steps));
        toast.error(errs.non_field_errors || errs.detail || 'Certaines informations sont à corriger.');
      } else {
        toast.error('Envoi impossible. Vérifiez votre connexion et réessayez.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setFormData(initialData);
    setVisited(new Set());
    setSubmitAttempted(false);
    setServerErrors({});
    setNumeroDossier('');
    setDraft(null);
    setStep(0);
  };

  // --- Candidature envoyée ---
  if (numeroDossier) {
    return (
      <Page>
        <div className="card success-page fade-in">
          <div className="success-icon">✓</div>
          <h2>Candidature envoyée</h2>
          <div className="dossier-number">{numeroDossier}</div>
          <p>Conservez ce numéro. Seuls les candidats présélectionnés seront contactés.</p>
          <button className="btn btn-secondary" style={{ marginTop: '1.5rem' }} onClick={restart}>
            Nouvelle candidature
          </button>
        </div>
      </Page>
    );
  }

  // --- Avis de recrutement ---
  if (step === 0) {
    if (campagneLoading) {
      return <Page><div className="loader"><div className="spinner" /></div></Page>;
    }
    const statut = statutCampagne(campagne);
    if (statut === 'a_venir') {
      return (
        <Page>
          <div className="card closed">
            <h2>Candidatures bientôt ouvertes</h2>
            <p>Le dépôt des candidatures ouvrira le {campagne.libelle_ouverture}.</p>
          </div>
        </Page>
      );
    }
    if (statut === 'cloturee') {
      return (
        <Page>
          <div className="card closed">
            <h2>Candidatures clôturées</h2>
            <p>Le dépôt était ouvert jusqu'au {campagne.libelle_cloture}.</p>
          </div>
        </Page>
      );
    }
    return (
      <Page>
        <div className="card avis fade-in">
          <h2 className="avis-title">Institut National de Santé Publique du Tchad (INSAPT) · ECV 2026</h2>
          <p>
            Pour l'Enquête Couverture Vaccinale 2026, l'INSAPT recrute des coordonnateurs provinciaux
            chargés de coordonner, superviser et suivre la collecte des données dans les provinces,
            sous la supervision de l'équipe centrale de l'enquête.
          </p>

          <h3>Missions</h3>
          <ul className="missions">
            {MISSIONS.map(m => <li key={m}>{m}</li>)}
          </ul>

          <h3>Critères</h3>
          <ol className="criteria">
            {CRITERES.map(c => (
              <li key={c.text}>
                {c.text}
                {c.obligatoire && <span className="tag-req">obligatoire</span>}
                {c.atout && <span className="tag-plus">atout</span>}
              </li>
            ))}
          </ol>

          <div className="avis-cta">
            <button className="btn btn-primary" type="button" onClick={() => goTo(draft ? RECAP_STEP : 1)}>
              {draft ? 'Reprendre ma candidature' : 'Postuler'}
            </button>
            <small>Environ 10 minutes · Recrutement gratuit</small>
          </div>
        </div>
      </Page>
    );
  }

  const StepComponent = step <= STEPS.length ? STEPS[step - 1].component : null;

  return (
    <Page>
      <nav className="stepper" aria-label="Étapes">
        {[...STEPS, { id: RECAP_STEP, label: 'Vérification' }].map(s => {
          const missing = s.id !== RECAP_STEP && missingByStep[s.id];
          const status = s.id === RECAP_STEP
            ? ''
            : !missing ? 'done' : showErrorsFor(s.id) ? 'error' : '';
          return (
            <button
              key={s.id}
              type="button"
              className={`step ${status} ${s.id === step ? 'active' : ''}`}
              onClick={() => goTo(s.id)}
              aria-current={s.id === step ? 'step' : undefined}
            >
              <span className="step-dot">{status === 'done' ? '✓' : status === 'error' ? '!' : s.id}</span>
              <span className="step-label">{s.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="progress" aria-hidden="true">
        <span style={{ width: `${(completedSteps / STEPS.length) * 100}%` }} />
      </div>

      <div className="card fade-in" key={step}>
        {StepComponent ? (
          <StepComponent data={formData} errors={errors} onChange={handleChange} />
        ) : (
          <Recap missingByStep={missingByStep} onGoTo={goTo} />
        )}

        <div className="form-actions">
          <button className="btn btn-ghost" type="button" onClick={() => goTo(step - 1)}>
            ← {step === 1 ? "L'avis" : 'Précédent'}
          </button>
          {step < RECAP_STEP ? (
            <button className="btn btn-primary" type="button" onClick={() => goTo(step + 1)}>
              Suivant →
            </button>
          ) : (
            <button
              className="btn btn-success"
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !isComplete}
            >
              {submitting ? 'Envoi…' : 'Envoyer ma candidature'}
            </button>
          )}
        </div>
      </div>
    </Page>
  );
};

const Recap = ({ missingByStep, onGoTo }) => {
  const remaining = Object.values(missingByStep).reduce((n, fields) => n + fields.length, 0);
  return (
    <>
      <h2>Vérification</h2>
      <p className="section-desc">
        {remaining === 0
          ? 'Votre dossier est complet.'
          : `${remaining} élément${remaining > 1 ? 's' : ''} à compléter avant l'envoi.`}
      </p>
      <ul className="recap">
        {STEPS.map(s => {
          const missing = missingByStep[s.id];
          return (
            <li key={s.id} className={missing ? '' : 'ok'}>
              <div>
                <div>{missing ? s.label : `✓ ${s.label}`}</div>
                {missing && (
                  <div className="missing">{missing.map(f => FIELD_LABELS[f] || f).join(', ')}</div>
                )}
              </div>
              {missing && (
                <button className="btn btn-secondary btn-sm" type="button" onClick={() => onGoTo(s.id)}>
                  Compléter
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <div className="notice">
        Toute fausse déclaration entraîne l'élimination. Le recrutement est entièrement gratuit.
      </div>
    </>
  );
};

const Page = ({ children }) => {
  const { campagne } = useCampagne();
  return (
    <div className="public">
      <header className="topbar">
        <div className="topbar-inner">
          <div>
            <div className="topbar-title">Recrutement des coordonnateurs provinciaux</div>
            <div className="topbar-org">Institut National de Santé Publique du Tchad (INSAPT) · ECV 2026</div>
          </div>
          {campagne && <span className="deadline-chip">Clôture : {campagne.libelle_cloture}</span>}
        </div>
      </header>
      <main className="container">
        {children}
        <TrustCard />
      </main>
      <SiteFooter />
    </div>
  );
};

export default RecruitmentForm;
